import { useState, useEffect } from "react";

import { Flame, Award, TrendingUp, Calendar } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";


interface StreakData {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  streak_start_date: string | null;
  total_active_days: number;
}

const MILESTONES = [
  { days: 7, label: '1 Week Warrior', icon: '🔥', color: 'bg-orange-500' },
  { days: 14, label: '2 Week Champion', icon: '⚡', color: 'bg-yellow-500' },
  { days: 30, label: 'Monthly Master', icon: '🏆', color: 'bg-blue-500' },
  { days: 60, label: '2 Month Legend', icon: '💎', color: 'bg-purple-500' },
  { days: 100, label: '100 Day Titan', icon: '👑', color: 'bg-pink-500' },
  { days: 365, label: 'Year-Long Hero', icon: '🌟', color: 'bg-linear-to-r from-yellow-400 to-orange-500' },
];

export const ActivityStreaksTracker = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState<StreakData | null>(null);

  useEffect(() => {
    fetchStreakData();
  }, [user]);

  const fetchStreakData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('activity_streaks')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setStreakData(data as StreakData);
      }
    } catch (error) {
      console.error('Error fetching streak data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEarnedMilestones = () => {
    if (!streakData) return [];
    return MILESTONES.filter(m => streakData.longest_streak >= m.days);
  };

  const getNextMilestone = () => {
    if (!streakData) return MILESTONES[0];
    return MILESTONES.find(m => m.days > streakData.current_streak) || MILESTONES[MILESTONES.length - 1];
  };

  const getDaysUntilNext = () => {
    const next = getNextMilestone();
    return next.days - (streakData?.current_streak || 0);
  };

  if (loading) {
    return (
      <Card className="animate-content-reveal">
        <CardHeader className="space-y-2">
          <Skeleton className="h-6 w-32" speed="fast" />
          <Skeleton className="h-4 w-48" speed="fast" delay={50} />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" speed="fast" delay={75} />
            <div className="space-y-2">
              <Skeleton className="h-8 w-24" speed="fast" delay={100} />
              <Skeleton className="h-4 w-32" speed="fast" delay={125} />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center p-3 rounded-lg border">
                <Skeleton className="h-6 w-8 mx-auto mb-2" speed="fast" delay={i * 60} />
                <Skeleton className="h-3 w-16 mx-auto" speed="fast" delay={i * 60 + 30} />
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-32" speed="fast" delay={200} />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" speed="fast" delay={i * 70} />
                  <Skeleton className="h-4 w-full" speed="fast" delay={i * 70 + 35} />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!streakData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            Activity Streaks
          </CardTitle>
          <CardDescription>Track your daily consistency</CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground mb-2">Start your streak today!</p>
          <p className="text-sm text-muted-foreground">
            Create any activity (session, message, memory, or project) to begin tracking
          </p>
        </CardContent>
      </Card>
    );
  }

  const earnedMilestones = getEarnedMilestones();
  const nextMilestone = getNextMilestone();
  const daysUntilNext = getDaysUntilNext();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          Activity Streaks
        </CardTitle>
        <CardDescription>Your consistency journey</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="text-center p-4 rounded-lg bg-linear-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
            <Flame className="h-8 w-8 mx-auto mb-2 text-orange-500" />
            <p className="text-3xl font-bold">{streakData.current_streak}</p>
            <p className="text-xs text-muted-foreground">Current Streak</p>
          </div>
          
          <div className="text-center p-4 rounded-lg bg-muted/50">
            <Award className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="text-3xl font-bold">{streakData.longest_streak}</p>
            <p className="text-xs text-muted-foreground">Longest Streak</p>
          </div>
          
          <div className="text-center p-4 rounded-lg bg-muted/50">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="text-3xl font-bold">{streakData.total_active_days}</p>
            <p className="text-xs text-muted-foreground">Total Active Days</p>
          </div>
        </div>

        {/* Next Milestone */}
        {daysUntilNext > 0 && (
          <div className="p-4 rounded-lg bg-linear-to-r from-primary/10 to-primary/5 border border-primary/20">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <p className="font-medium">Next Milestone</p>
                <p className="text-sm text-muted-foreground">
                  {nextMilestone.icon} {nextMilestone.label} - {daysUntilNext} days to go!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Earned Milestones */}
        {earnedMilestones.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Award className="h-4 w-4" />
              Earned Badges ({earnedMilestones.length}/{MILESTONES.length})
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {earnedMilestones.map((milestone) => (
                <div
                  key={milestone.days}
                  className="flex items-center gap-3 p-3 rounded-lg bg-linear-to-r from-muted to-background border"
                >
                  <div className="text-2xl">{milestone.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{milestone.label}</p>
                    <p className="text-xs text-muted-foreground">{milestone.days} days</p>
                  </div>
                  <Badge variant="secondary" className="shrink-0">✓</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Milestones */}
        {earnedMilestones.length < MILESTONES.length && (
          <div>
            <h3 className="font-semibold mb-3 text-muted-foreground">Locked Badges</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {MILESTONES.filter(m => m.days > streakData.longest_streak).map((milestone) => (
                <div
                  key={milestone.days}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-dashed opacity-60"
                >
                  <div className="text-2xl grayscale">{milestone.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{milestone.label}</p>
                    <p className="text-xs text-muted-foreground">{milestone.days} days</p>
                  </div>
                  <Badge variant="outline" className="shrink-0">🔒</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
